import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import PlayerDemo from "@/pages/detail/components/PlayerDemo";
import FavoriteButton from "@/pages/detail/components/FavoriteButton";
import { mediaTypeLabels } from "@/mocks/movies";
import { formatViews, getMovieById, getRelatedMovies } from "@/utils/movie";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);

  if (!movie) {
    return (
      <div className="mx-auto max-w-page px-4 md:px-8 pt-10 md:pt-16">
        <Breadcrumb items={[{ label: "影视分类", path: "/category" }, { label: "未找到" }]} />
        <div className="mt-8 rounded-lg bg-background-100 border border-background-300/60 py-20 px-6 text-center">
          <span className="mx-auto w-16 h-16 rounded-full bg-background-200/80 flex items-center justify-center text-foreground-600">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading font-bold text-xl text-foreground-950">没有找到这部影视</h1>
          <p className="mt-2 text-sm text-foreground-600">该资源可能已被移除，或链接不正确。</p>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回影视分类
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 6);

  return (
    <div className="mx-auto max-w-page px-4 md:px-8 pt-8 md:pt-12">
      <Breadcrumb
        items={[
          { label: "影视分类", path: "/category" },
          { label: mediaTypeLabels[movie.type], path: `/category?type=${movie.type}` },
          { label: movie.title },
        ]}
      />

      {/* Hero band */}
      <section className="mt-6 relative rounded-lg overflow-hidden border border-background-300/60">
        <div className="absolute inset-0">
          <img
            src={movie.heroImage ?? movie.cover}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60"></div>
        </div>
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 p-5 md:p-10">
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="w-full max-w-[280px] lg:max-w-none mx-auto rounded-lg overflow-hidden border border-background-300/60 aspect-[2/3] bg-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title} 高清封面`}
                title={`${movie.title} - 午夜免费视频`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-8 xl:col-span-9 flex flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-label font-semibold">
                {mediaTypeLabels[movie.type]}
              </span>
              {movie.featured && (
                <span className="px-2.5 py-1 rounded-md bg-accent-500/20 border border-accent-400/40 text-accent-300 text-xs font-label">
                  午夜焦点
                </span>
              )}
            </div>

            <h1 className="mt-4 font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
              {movie.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 italic">“{movie.tagline}”</p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-700">
              <span className="flex items-center gap-1.5 text-primary-400 font-semibold">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)}
              </span>
              <span>{movie.year}</span>
              <span className="text-foreground-500">·</span>
              <span>{movie.region}</span>
              <span className="text-foreground-500">·</span>
              <span>{movie.duration}</span>
              <span className="text-foreground-500">·</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {formatViews(movie.views)}次播放
              </span>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <p className="text-foreground-600">
                导演：<span className="text-foreground-900">{movie.director}</span>
              </p>
              <p className="text-foreground-600">
                类型：<span className="text-foreground-900">{movie.genres.join(" / ")}</span>
              </p>
              <p className="text-foreground-600 sm:col-span-2">
                主演：<span className="text-foreground-900">{movie.cast.join("、")}</span>
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#play-area"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill text-xl"></i>
                立即观看
              </a>
              <FavoriteButton movieId={movie.id} />
              <Link
                to={`/category?type=${movie.type}`}
                className="inline-flex items-center gap-2 h-12 px-5 rounded-md bg-background-200/70 border border-background-300/70 text-foreground-800 text-sm font-medium hover:border-primary-500/60 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回分类
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main content + sidebar */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8" id="play-area">
          <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950">剧情简介</h2>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">{movie.synopsis}</p>

          <h2 className="mt-10 font-heading font-bold text-lg md:text-xl text-foreground-950">在线播放</h2>
          <p className="mt-2 text-xs md:text-sm text-foreground-600">以下为播放界面演示，用于展示播放器交互效果。</p>
          <div className="mt-4">
            <PlayerDemo poster={movie.heroImage ?? movie.cover} title={movie.title} />
          </div>

          <div className="mt-6 rounded-lg bg-background-100 border border-background-300/60 p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-md bg-secondary-500/20 text-secondary-300 flex items-center justify-center">
                <i className="ri-information-line text-xl"></i>
              </span>
              <p className="text-xs md:text-sm text-foreground-600">
                本页所有影视信息为内容演示，标题、封面与简介均与列表页保持一致。
              </p>
            </div>
            <Link to="/category" className="text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap">
              返回分类首页
            </Link>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950">相关推荐</h2>
          <p className="mt-2 text-xs md:text-sm text-foreground-600">基于相同类型与标签为你挑选。</p>
          <div className="mt-4 flex flex-col gap-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex gap-3 rounded-lg bg-background-100 border border-background-300/60 p-3 transition-colors hover:border-primary-500/60 cursor-pointer"
              >
                <div className="relative w-20 h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title} 推荐封面`}
                    title={`${item.title} - 午夜免费视频`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex flex-col justify-center">
                  <h3 className="text-sm font-medium text-foreground-950 truncate group-hover:text-primary-400 transition-colors">
                    {item.title}
                  </h3>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-600">
                    <span className="flex items-center gap-1 text-primary-400 font-semibold">
                      <i className="ri-star-fill text-xs"></i>
                      {item.rating.toFixed(1)}
                    </span>
                    <span>{item.year}</span>
                    <span className="text-foreground-500">·</span>
                    <span className="truncate">{item.region}</span>
                  </div>
                  <p className="mt-2 text-xs text-foreground-500 line-clamp-2">{item.synopsis}</p>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}